import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import discountCodesApi from "views/discounts/api/api";
import {
  CreateDiscountRequest,
  UpdateDiscountRequest,
  GetDiscountsResponse,
  UseDiscountsOptions,
  UseInfiniteDiscountsOptions
} from "views/discounts/types";

const DISCOUNTS_BASE_KEY = "DISCOUNTS";

const discountKeys = {
  all: [DISCOUNTS_BASE_KEY] as const,
  list: (filters: Record<string, any>) =>
    [...discountKeys.all, "list", filters] as const,
  detail: (id: number) => [...discountKeys.all, "detail", id] as const
};

export const useDiscounts = (options: UseDiscountsOptions = {}) => {
  const {
    active,
    restriction_type,
    discount_type,
    enabled,
    template_id,
    per_page
  } = options;
  const filters = { active, restriction_type, discount_type, template_id };

  return useQuery({
    queryKey: discountKeys.list(filters),
    queryFn: () =>
      discountCodesApi.getDiscounts({
        active,
        restriction_type,
        discount_type,
        per_page
      }),
    select: (data: GetDiscountsResponse) => {
      const filteredItems = data.items.filter(
        (discount) => !discount.code.startsWith("CUSTOM-")
      );

      if (template_id && restriction_type === "template")
        return filteredItems.filter((discount) =>
          discount.templates?.some((t) => t.id === template_id)
        );
      return filteredItems;
    },
    enabled: enabled ? true : undefined
  });
};

export const useInfiniteDiscounts = (
  options: UseInfiniteDiscountsOptions = {}
) => {
  const {
    active,
    restriction_type,
    discount_type,
    enabled,
    template_id,
    per_page = 10
  } = options;
  const filters = { active, restriction_type, discount_type, template_id };

  const query = useInfiniteQuery<GetDiscountsResponse>({
    queryKey: [...discountKeys.list(filters), "infinite"],
    queryFn: ({ pageParam = 1 }) =>
      discountCodesApi.getDiscounts({
        active,
        restriction_type,
        discount_type,
        per_page,
        page: Number(pageParam)
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: GetDiscountsResponse | undefined) =>
      lastPage?.has_more ? (lastPage.current_page || 0) + 1 : undefined,
    enabled: enabled !== false
  });

  // Apply filtering in a separate step to preserve pagination metadata
  const filteredData = query.data
    ? {
        pages: query.data.pages.map((page) => ({
          ...page,
          items: page.items
            .filter((discount) => !discount.code.startsWith("CUSTOM-"))
            .filter((discount) => {
              if (template_id && restriction_type === "template")
                return discount.templates?.some((t) => t.id === template_id);
              return true;
            })
        })),
        pageParams: query.data.pageParams
      }
    : undefined;

  return {
    ...query,
    data: filteredData
  };
};

export const useCreateDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...discountKeys.all, "CREATE"],
    mutationFn: (data: CreateDiscountRequest) =>
      discountCodesApi.createDiscount(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: discountKeys.all });

      toast.success(`Discount code "${variables.code}" created successfully!`);
    },
    onError: (error) => {
      toast.error("Failed to create discount code.", getErrorMessage(error));
    }
  });
};

export const useUpdateDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...discountKeys.all, "UPDATE"],
    mutationFn: ({
      discountId,
      data
    }: {
      discountId: number;
      data: UpdateDiscountRequest;
    }) => discountCodesApi.updateDiscount(discountId, data),
    onSuccess: (updatedDiscount, variables) => {
      queryClient.setQueryData(
        discountKeys.detail(variables.discountId),
        updatedDiscount
      );

      queryClient.invalidateQueries({ queryKey: discountKeys.all });
    },
    onError: () => {
      toast.error("Failed to update discount code.");
    }
  });
};

export const useDeleteDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...discountKeys.all, "DELETE"],
    mutationFn: (discountId: number) =>
      discountCodesApi.deleteDiscount(discountId),
    onSuccess: (deletedDiscount) => {
      queryClient.invalidateQueries({ queryKey: discountKeys.all });
      queryClient.removeQueries({
        queryKey: discountKeys.detail(deletedDiscount.id)
      });

      toast.success("Discount code deleted successfully!");
    },
    onError: () => {
      toast.error("Failed to delete discount code.");
    }
  });
};

export const useGenerateDiscountCode = () => {
  return useMutation({
    mutationKey: [...discountKeys.all, "GENERATE"],
    mutationFn: () => discountCodesApi.generateDiscountCode(),
    onError: () => {
      toast.error("Failed to generate discount code.");
    }
  });
};

// Application discount mutations
export const useApplyDiscountToApplicant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...discountKeys.all, "APPLY"],
    mutationFn: ({
      applicationId,
      applicantId,
      discountData,
      isApplicantEntity = true
    }: {
      applicationId: number;
      applicantId: number;
      discountData: {
        discount_code: string;
        custom_discount_type?: string;
        custom_discount_amount?: number;
        custom_end_date?: string;
      };
      isApplicantEntity?: boolean;
    }) =>
      discountCodesApi.applyDiscountToApplicant(
        applicationId,
        applicantId,
        discountData,
        isApplicantEntity
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(variables.applicationId)]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_ID_REPORT", String(variables.applicationId)]
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.all });
    },
    onError: () => {
      toast.error("Failed to apply discount.");
    }
  });
};

export const useRemoveDiscountFromApplicant = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...discountKeys.all, "REMOVE"],
    mutationFn: ({
      applicationId,
      applicantId,
      isApplicantEntity = true
    }: {
      applicationId: number;
      applicantId: number;
      isApplicantEntity?: boolean;
    }) =>
      discountCodesApi.removeDiscountFromApplicant(
        applicationId,
        applicantId,
        isApplicantEntity
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", String(variables.applicationId)]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION_ID_REPORT", String(variables.applicationId)]
      });
      queryClient.invalidateQueries({ queryKey: discountKeys.all });

      toast.success("Discount removed successfully!");
    },
    onError: () => {
      toast.error("Failed to remove discount.");
    }
  });
};
